import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, Plus, Pencil, Trash2, CopyPlus, Eye } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { RichTextEditor, RichContent } from "@/components/RichTextEditor";
import FormBuilder from "@/components/FormBuilder";
import FormRenderer from "@/components/FormRenderer";
import { SAKRAMENT_LABELS } from "@/lib/constants";

const SAKS = ["TA", "EK", "FI", "EH"];

export default function DatenschutzVorlagen() {
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  const load = () => api.get("/datenschutz-templates").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => setEdit({ sakrament: "EK", name: "", text: "", text_end: "", fields: [] });
  const duplicate = (t) => { setShowPreview(false); setEdit({ sakrament: t.sakrament, name: `${t.name} (Kopie)`, text: t.text || "", text_end: t.text_end || "", fields: (t.fields || []) }); };
  const save = async () => {
    if (!edit.name.trim()) return toast.error("Bitte einen Namen vergeben.");
    try {
      const payload = { sakrament: edit.sakrament, name: edit.name, text: edit.text, text_end: edit.text_end || "", fields: edit.fields || [] };
      if (edit.id) await api.put(`/datenschutz-templates/${edit.id}`, payload);
      else await api.post("/datenschutz-templates", payload);
      toast.success("Gespeichert."); setEdit(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const del = async (id) => {
    if (!window.confirm("Diese Datenschutzerklärung wirklich löschen?")) return;
    await api.delete(`/datenschutz-templates/${id}`); toast.success("Gelöscht."); load();
  };

  return (
    <div className="px-4 sm:px-8 py-6 sm:py-10" data-testid="ds-templates-page">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-3"><ShieldCheck className="text-primary" /><h1 className="text-3xl font-serif">Datenschutzerklärungen</h1></div>
        <Button onClick={openNew} data-testid="ds-new-button"><Plus size={16} className="mr-1" /> Neue Vorlage</Button>
      </div>
      <p className="text-muted-foreground mb-6 text-sm">Standardisierte Datenschutzerklärungen je Sakrament. Sie können im Anmeldeformular über den Feldtyp „Datenschutzerklärung" eingefügt werden.</p>

      <div className="space-y-8">
        {SAKS.map((s) => {
          const list = items.filter((t) => t.sakrament === s);
          return (
            <div key={s} data-testid={`ds-group-${s}`}>
              <h2 className="text-lg font-semibold mb-3">{SAKRAMENT_LABELS[s]}</h2>
              {list.length === 0 ? (
                <p className="text-sm text-muted-foreground">Noch keine Vorlage.</p>
              ) : (
                <div className="grid sm:grid-cols-2 gap-3">
                  {list.map((t) => (
                    <div key={t.id} className="bg-white border border-border rounded-lg shadow-sm p-4" data-testid={`ds-item-${t.id}`}>
                      <div className="flex items-center justify-between mb-2">
                        <p className="font-medium">{t.name}</p>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => setEdit({ id: t.id, sakrament: t.sakrament, name: t.name, text: t.text, text_end: t.text_end || "", fields: t.fields || [] })} data-testid={`ds-edit-${t.id}`}><Pencil size={15} /></Button>
                          <Button variant="ghost" size="sm" onClick={() => duplicate(t)} title="Duplizieren" data-testid={`ds-duplicate-${t.id}`}><CopyPlus size={15} /></Button>
                          <Button variant="ghost" size="sm" onClick={() => del(t.id)} data-testid={`ds-delete-${t.id}`}><Trash2 size={15} className="text-destructive" /></Button>
                        </div>
                      </div>
                      <div className="max-h-32 overflow-y-auto text-xs text-slate-600 border-t border-border pt-2">
                        {t.text ? <RichContent html={t.text} className="text-xs" /> : <span className="text-muted-foreground">Kein Text.</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{edit?.id ? "Datenschutzerklärung bearbeiten" : "Neue Datenschutzerklärung"}</DialogTitle></DialogHeader>
          {edit && (
            <div className="space-y-4 py-2">
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Sakrament</Label>
                  <Select value={edit.sakrament} onValueChange={(v) => setEdit({ ...edit, sakrament: v })}>
                    <SelectTrigger data-testid="ds-edit-sakrament"><SelectValue /></SelectTrigger>
                    <SelectContent>{SAKS.map((s) => <SelectItem key={s} value={s}>{SAKRAMENT_LABELS[s]}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Name der Vorlage</Label>
                  <Input value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} placeholder="z.B. Firmung 2027" data-testid="ds-edit-name" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Einleitungstext (optional, Rich-Text)</Label>
                <RichTextEditor value={edit.text} onChange={(v) => setEdit({ ...edit, text: v })} placeholder="Text der Einwilligungserklärung…" testid="ds-edit-text" />
              </div>
              <div className="space-y-2">
                <Label>Formularfelder (werden im Anmeldeformular ausgefüllt)</Label>
                <FormBuilder fields={edit.fields || []} onChange={(f) => setEdit({ ...edit, fields: f })} allowDatenschutz={false} sakrament={edit.sakrament} />
              </div>
              <div className="space-y-2">
                <Label>Schlusstext (optional, Rich-Text – erscheint am Ende, vor der Zustimmung)</Label>
                <RichTextEditor value={edit.text_end} onChange={(v) => setEdit({ ...edit, text_end: v })} placeholder="z.B. Hinweise zum Widerruf…" testid="ds-edit-text-end" />
              </div>
              <div className="space-y-2 border-t border-border pt-3">
                <Button type="button" variant="outline" size="sm" onClick={() => setShowPreview((v) => !v)} data-testid="ds-preview-toggle"><Eye size={15} className="mr-1" /> {showPreview ? "Vorschau ausblenden" : "Vorschau anzeigen"}</Button>
                {showPreview && (
                  <div className="border border-border rounded-lg p-4 bg-slate-50" data-testid="ds-preview">
                    <FormRenderer courseId="preview" skipRequired submitting={false} submitLabel="Nur Vorschau" onSubmit={() => {}}
                      fields={[{ id: "__pds__", type: "datenschutz", template_id: "__pv__" }]}
                      dsTemplates={[{ id: "__pv__", text: edit.text, text_end: edit.text_end, fields: edit.fields || [] }]}
                      consents={[{ key: "psb1", label: "Personensorgeberechtigte 1" }, { key: "psb2", label: "Personensorgeberechtigte 2" }]} />
                  </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter><Button onClick={save} data-testid="ds-save-button">Speichern</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
